import { LoanSummary, AgreementCheckbox } from "../../components"
import { useAppState, useConsumerCalcs } from "../../hooks"
import { SolarConsumerEducationLink } from "./consumer-resource-links"
import { SubformProps } from "."

export const SubmitLoanApplication = ({
  formData,
  onSubmit,
  update,
}: Partial<SubformProps>) => {
  const { tenant } = useAppState()
  const { loanTermInYears, rate, amortization } = useConsumerCalcs(formData)
  const { incentiveSelected } = formData
  const mcgbProduct = formData.incentive?.incentiveProducts.find(
    p => p.loanTerm === formData.loanDurationMonths
  )
  const showIncentiveCopy = Boolean(incentiveSelected && formData.incentive && mcgbProduct)

  const nonIncentiveText = ` at a ${rate}% interest rate`
  const incentiveText = showIncentiveCopy ? ` with an incentive rate of ${mcgbProduct.incentivizedRate}% for the first 15 years **, 
    followed by a rate of ${mcgbProduct.nonIncentiveRate}% for the remaining years` : ''

  return (
    <div className="sa-summary mt-4">
      <p className="mb-0">
        You're applying for a {loanTermInYears}-year loan ({formData.loanDurationMonths} months)
        {showIncentiveCopy ? incentiveText : nonIncentiveText}. Here's a quick summary of your loan details:
      </p>
      {showIncentiveCopy && formData.incentive?.termsUrl ?
        <p className="text-small mb-4 mt-2 fst-italic">
          **With qualifying incentive. Incentive terms can be found <a target="_blank" href={formData.incentive.termsUrl} rel="noreferrer">here</a>.
        </p> : null}
      <div className="d-flex flex-column gap-3 mt-4 mb-2">
        <LoanSummary
          formData={formData}
          incentive={formData.incentive}
          showApr={formData.projectType === 'residential' && !formData.incentive}
          amortization={amortization}
        />
        <SolarConsumerEducationLink />
      </div>
      <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesToProceedWithHardCreditInquiry"
      >
        I understand that by submitting my loan application, this will
        result in a hard credit inquiry, which can impact my credit score.
      </AgreementCheckbox>
      <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesToWarningAgainstUsingOtherFinancing"
      >
        I understand that {tenant.config.name} clean energy loans are not to be used alongside any other
        financing products, such as a lease or power purchase agreement (PPA). Using funds for any purpose
        other than the purchase and installation of clean energy improvements to be <b>owned</b> by the customer
        will cause the loan to default and trigger remedies, including but not limited to, acceleration
        of loan repayment.
      </AgreementCheckbox>
      <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesTo6MonthNoPaymentPeriod"
      >
        I understand no payments are required during the first six months and that interest
        will continue to accrue and be added to my loan balance during this period.
      </AgreementCheckbox>
      <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesToIncomeAndFinancialInformation"
      >
        I certify that the income and financial information I have provided is true, complete,
        and accurate to the best of my knowledge. I understand that providing false, misleading,
        or incomplete information may result in denial of my application or other appropriate action.
        I agree to promptly notify the financial institution if any material changes occur to the
        information provided prior to the completion of the credit decision or funding of any approved loan.
      </AgreementCheckbox>
      <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesToLenderInspection"
      >
        I understand that {tenant.config.name} or its service provider(s) may conduct one or more Lender Inspections by physically visiting the property to verify information provided in connection with a funding request. I agree to cooperate with the Lender Inspection and provide access to the property (if needed) for the Lender Inspection.
      </AgreementCheckbox>
      <p className="mb-4">
        By submitting your application request you certify that complete and accurate responses have
        been provided for all questions on the application, and agree to
        allow {tenant.config.name} to verify your personal information and check your credit.
      </p>
      <div className="buttons text-start">
        <button
          disabled={[
            formData.agreesToProceedWithHardCreditInquiry?.agrees,
            formData.agreesToLenderInspection?.agrees,
            formData.attestConsumerResourceUnderstanding?.agrees,
            formData.agreesToWarningAgainstUsingOtherFinancing?.agrees,
            formData.agreesTo6MonthNoPaymentPeriod?.agrees,
            formData.agreesToIncomeAndFinancialInformation?.agrees
          ].some(v => v !== true)}
          type="button"
          className="btn btn-primary"
          onClick={() => onSubmit()}
        >Submit Loan Application</button>
        {process.env.REACT_APP_NODE_ENV === 'test' ? <>
          <button
            disabled={!formData.attestConsumerResourceUnderstanding?.agrees}
            type="button"
            className="btn btn-secondary"
            onClick={() => onSubmit({ test: true })}
          >Test Submit</button>
        </> : null}
      </div>
      {process.env.REACT_APP_NODE_ENV === 'test' ? <div className="alert alert-secondary mt-3">
        Clicking "Test Submit" will pull DIT, send docs, and simulate the user experience,
        but will NOT actually submit to the lender; e.g., account numbers will be faked.
      </div> : null}
    </div>
  )
}
